import React, { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, Form, Label, Select } from '@shared/components';
import { makePayerPairOverrideSchema, type PayerPairOverrideValues } from '../schemas/payerPairOverride.schema';
import type { PayerPairOverridePayload, RefLabel } from '../services/productFees';
import { targetName, type DefaultPayerPair } from '../utils/routablePayerPairs';

export type ReferenceStatus = 'loading' | 'error' | 'ready';

interface Props {
    targets: RefLabel[];
    referenceStatus?: ReferenceStatus;
    defaultPair: DefaultPayerPair | null;
    initial?: PayerPairOverridePayload;
    onSubmit: (payload: PayerPairOverridePayload) => void;
    isLoading: boolean;
    error?: string | null;
    onCancel: () => void;
}

const FIELDS = [
    { name: 'paid_by_id', label: 'Paid By', placeholder: 'Select paid by' },
    { name: 'paid_to_id', label: 'Paid To', placeholder: 'Select paid to' },
] as const;

const ReferenceNotice: React.FC<{ status: Exclude<ReferenceStatus, 'ready'> }> = ({ status }) =>
    status === 'loading'
        ? <Alert variant="info" role="status">Loading payer targets...</Alert>
        : <Alert variant="error">Payer targets could not be loaded. Close this dialog and try again.</Alert>;

export const PayerPairOverrideForm: React.FC<Props> = ({
    targets,
    referenceStatus = 'ready',
    defaultPair,
    initial,
    onSubmit,
    isLoading,
    error,
    onCancel,
}) => {
    const schema = useMemo(() => makePayerPairOverrideSchema(targets, defaultPair), [targets, defaultPair]);
    const { control, handleSubmit } = useForm<PayerPairOverrideValues>({
        resolver: zodResolver(schema),
        defaultValues: { paid_by_id: initial?.paid_by_id ?? '', paid_to_id: initial?.paid_to_id ?? '' },
    });

    return (
        <Form onSubmit={handleSubmit((values) => onSubmit({ paid_by_id: values.paid_by_id, paid_to_id: values.paid_to_id }))} className="space-y-4">
            {error && <Alert variant="error">{error}</Alert>}

            {referenceStatus !== 'ready' ? (
                <ReferenceNotice status={referenceStatus} />
            ) : (
                <>
                    <p className="text-sm text-mp-muted">
                        Default pair for this fee type:{' '}
                        {defaultPair
                            ? `${targetName(targets, defaultPair.paidById)} to ${targetName(targets, defaultPair.paidToId)}`
                            : 'none configured'}
                    </p>

                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        {FIELDS.map(({ name, label, placeholder }) => (
                            <div key={name}>
                                <Label htmlFor={name} required>{label}</Label>
                                <Controller
                                    name={name}
                                    control={control}
                                    render={({ field, fieldState: { error: fe } }) => (
                                        <Select
                                            id={name}
                                            ariaLabel={label}
                                            placeholder={placeholder}
                                            value={field.value}
                                            onChange={field.onChange}
                                            onBlur={field.onBlur}
                                            disabled={isLoading}
                                            error={!!fe}
                                            hint={fe?.message}
                                            options={targets.map((t) => ({ value: t.id, label: t.name }))}
                                        />
                                    )}
                                />
                            </div>
                        ))}
                    </div>
                </>
            )}

            <div className="flex justify-end gap-2">
                <Button type="button" variant="secondary" onClick={onCancel} disabled={isLoading}>Cancel</Button>
                <Button type="submit" variant="primary" disabled={isLoading || referenceStatus !== 'ready'}>Save override</Button>
            </div>
        </Form>
    );
};
